<template>
  <div class="article-page">
    <!-- 面包屑 -->
    <nav class="breadcrumb-nav">
      <router-link to="/">首页</router-link>
      <span class="separator">/</span>
      <router-link to="/research">研究中心</router-link>
      <span class="separator">/</span>
      <span class="current">主数据管理实践</span>
    </nav>

    <article class="container">
      <!-- 头部 -->
      <header class="article-header">
        <div class="article-tags">
          <span class="tag">数据治理知识库</span>
          <span class="tag">主数据</span>
          <span class="tag">MDM</span>
        </div>
        <h1 class="article-title">主数据管理实践</h1>
        <p class="article-subtitle">从0到1建立企业级主数据管理体系 · 标准制定 · 系统建设 · 运营治理</p>
        <div class="article-meta">
          <span><i class="far fa-calendar-alt"></i> 2026-01-08</span>
          <span><i class="far fa-clock"></i> 阅读约 16 分钟</span>
          <span><i class="far fa-user"></i> 古杉研究中心</span>
        </div>
      </header>

      <!-- 导读 -->
      <section class="intro-card">
        <div class="intro-icon">🗂️</div>
        <div>
          <h3>导读</h3>
          <p>主数据是企业最核心的共享数据资产——客户、产品、供应商、组织、物料等，它们跨越系统边界、贯穿业务流程。主数据质量直接影响企业经营决策的准确性和业务系统的运转效率。本文系统介绍主数据管理的全生命周期方法论与实战经验。</p>
        </div>
      </section>

      <!-- 目录 -->
      <nav class="toc">
        <h3>📑 目录</h3>
        <ul>
          <li><a href="#section1">一、什么是主数据</a></li>
          <li><a href="#section2">二、主数据管理的核心挑战</a></li>
          <li><a href="#section3">三、客户主数据标准模板</a></li>
          <li><a href="#section4">四、产品主数据治理流程</a></li>
          <li><a href="#section5">五、组织架构主数据设计</a></li>
          <li><a href="#section6">六、主数据质量监控方案</a></li>
          <li><a href="#section7">七、MDM系统选型与实施建议</a></li>
        </ul>
      </nav>

      <!-- 正文 -->
      <div class="article-body">

        <!-- 第一章 -->
        <section id="section1" class="chapter">
          <h2>一、什么是主数据</h2>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">🎯</div>
              <div>
                <h4>主数据的定义</h4>
                <p>主数据（Master Data）是指在企业内跨多个业务系统共享使用的、相对稳定的、高价值的核心业务实体数据。它是企业数据资产的"基座"。</p>
              </div>
            </div>
          </div>

          <h3>主数据的特征</h3>
          <div class="feature-grid">
            <div class="feature-card" v-for="f in mdFeatures" :key="f.title">
              <div class="feature-icon">{{ f.icon }}</div>
              <h4>{{ f.title }}</h4>
              <p>{{ f.desc }}</p>
            </div>
          </div>

          <h3>企业常见主数据类型</h3>
          <div class="md-types">
            <div class="md-type-card" v-for="t in mdTypes" :key="t.name">
              <div class="md-type-icon" :style="{ background: t.color }">{{ t.icon }}</div>
              <div>
                <h4>{{ t.name }}</h4>
                <p>{{ t.desc }}</p>
                <div class="md-type-examples">
                  <span v-for="(e, i) in t.examples" :key="i">{{ e }}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 第二章 -->
        <section id="section2" class="chapter">
          <h2>二、主数据管理的核心挑战</h2>

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-number">72%</div>
              <div class="stat-label">的企业存在"一客多码"问题</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">45%</div>
              <div class="stat-label">的物料编码在多系统间不一致</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">30%</div>
              <div class="stat-label">的报表数据偏差源于主数据不一致</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">6个月+</div>
              <div class="stat-label">新系统主数据对齐的平均耗时</div>
            </div>
          </div>

          <div class="challenge-list">
            <div class="challenge-item" v-for="(c, i) in challenges" :key="i">
              <div class="challenge-marker" :style="{ background: c.color }">{{ i + 1 }}</div>
              <div class="challenge-content">
                <h4>{{ c.title }}</h4>
                <p>{{ c.desc }}</p>
                <div class="challenge-example">典型场景：{{ c.example }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- 第三章 -->
        <section id="section3" class="chapter">
          <h2>三、客户主数据标准模板</h2>
          <p>客户主数据是企业最关键的主数据之一，涉及销售、营销、服务、财务等多个业务域。以下是经过多家企业验证的客户主数据标准模板。</p>

          <h3>3.1 客户主数据信息模型</h3>
          <div class="data-model">
            <div class="model-layer">
              <div class="layer-title">基本信息层</div>
              <div class="layer-fields">
                <span v-for="f in customerBasicFields" :key="f">{{ f }}</span>
              </div>
            </div>
            <div class="model-layer">
              <div class="layer-title">联系信息层</div>
              <div class="layer-fields">
                <span v-for="f in customerContactFields" :key="f">{{ f }}</span>
              </div>
            </div>
            <div class="model-layer">
              <div class="layer-title">业务信息层</div>
              <div class="layer-fields">
                <span v-for="f in customerBizFields" :key="f">{{ f }}</span>
              </div>
            </div>
            <div class="model-layer">
              <div class="layer-title">财务信息层</div>
              <div class="layer-fields">
                <span v-for="f in customerFinFields" :key="f">{{ f }}</span>
              </div>
            </div>
          </div>

          <h3>3.2 客户编码规则</h3>
          <div class="code-rule">
            <div class="code-segment" v-for="seg in codeSegments" :key="seg.name">
              <div class="code-segment-header" :style="{ background: seg.color }">{{ seg.name }}</div>
              <div class="code-segment-body">
                <p>{{ seg.desc }}</p>
                <div class="code-example">{{ seg.example }}</div>
              </div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">💡</div>
            <div>
              <h4>编码设计原则</h4>
              <p>①唯一性：一个客户一个编码，全集团统一；②无含义性：避免将业务属性编入编码，防止变更时编码失效；③可扩展：预留编码空间，满足未来增长需求。</p>
            </div>
          </div>
        </section>

        <!-- 第四章 -->
        <section id="section4" class="chapter">
          <h2>四、产品主数据治理流程</h2>

          <h3>4.1 产品主数据治理全流程</h3>
          <div class="flow-steps">
            <div class="flow-step" v-for="(step, i) in productFlow" :key="i">
              <div class="flow-step-number" :style="{ background: step.color }">{{ i + 1 }}</div>
              <div class="flow-step-content">
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
                <div class="flow-step-roles">
                  <span class="role-tag" v-for="role in step.roles" :key="role">{{ role }}</span>
                </div>
              </div>
            </div>
          </div>

          <h3>4.2 产品BOM数据治理要点</h3>
          <div class="bom-grid">
            <div class="bom-card">
              <h4>📐 BOM结构标准化</h4>
              <ul>
                <li>统一BOM层级定义（产成品 → 半成品 → 原材料）</li>
                <li>明确虚拟件与实物件区分规则</li>
                <li>制定BOM变更审批流程</li>
              </ul>
            </div>
            <div class="bom-card">
              <h4>🔗 BOM跨系统一致性</h4>
              <ul>
                <li>PLM → ERP → MES的BOM传递规范</li>
                <li>工程BOM与制造BOM的映射关系</li>
                <li>BOM版本管理与生效机制</li>
              </ul>
            </div>
            <div class="bom-card">
              <h4>✅ BOM质量检查</h4>
              <ul>
                <li>必填字段完整性检查</li>
                <li>用量与损耗率合理性校验</li>
                <li>替代料关系一致性验证</li>
              </ul>
            </div>
          </div>
        </section>

        <!-- 第五章 -->
        <section id="section5" class="chapter">
          <h2>五、组织架构主数据设计</h2>

          <p>组织架构主数据是连接"人、财、物、事"的核心纽带。它的质量直接影响权限管理、审批流程、报表统计等多个业务场景。</p>

          <h3>5.1 组织架构模型设计</h3>
          <div class="org-model">
            <div class="org-level" v-for="level in orgLevels" :key="level.name">
              <div class="org-level-badge" :style="{ background: level.color }">{{ level.name }}</div>
              <div class="org-level-info">
                <p class="org-level-desc">{{ level.desc }}</p>
                <div class="org-level-attrs">
                  <span v-for="attr in level.attrs" :key="attr">{{ attr }}</span>
                </div>
              </div>
            </div>
          </div>

          <h3>5.2 常见问题与解决方案</h3>
          <div class="qa-grid">
            <div class="qa-card" v-for="qa in orgQAs" :key="qa.q">
              <div class="qa-question">❓ {{ qa.q }}</div>
              <div class="qa-answer">✅ {{ qa.a }}</div>
            </div>
          </div>
        </section>

        <!-- 第六章 -->
        <section id="section6" class="chapter">
          <h2>六、主数据质量监控方案</h2>

          <h3>6.1 质量度量维度</h3>
          <div class="quality-dims">
            <div class="quality-dim" v-for="dim in qualityDims" :key="dim.name">
              <div class="dim-header" :style="{ borderColor: dim.color }">
                <div class="dim-icon">{{ dim.icon }}</div>
                <h4>{{ dim.name }}</h4>
              </div>
              <p>{{ dim.desc }}</p>
              <div class="dim-rule">规则示例：{{ dim.rule }}</div>
              <div class="dim-target">目标值：{{ dim.target }}</div>
            </div>
          </div>

          <h3>6.2 监控闭环机制</h3>
          <div class="loop-flow">
            <div class="loop-step" v-for="(step, i) in monitorLoop" :key="i">
              <div class="loop-marker" :style="{ background: step.color }">{{ step.icon }}</div>
              <h4>{{ step.title }}</h4>
              <p>{{ step.desc }}</p>
            </div>
          </div>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">📊</div>
              <div>
                <h4>主数据质量看板核心指标</h4>
                <p>①主数据一致性率（跨系统）；②主数据完整性率（必填字段）；③重复率（去重后/去重前）；④变更及时率（变更工单SLA达标率）；⑤数据标准覆盖率。</p>
              </div>
            </div>
          </div>
        </section>

        <!-- 第七章 -->
        <section id="section7" class="chapter">
          <h2>七、MDM系统选型与实施建议</h2>

          <h3>7.1 MDM系统核心能力</h3>
          <div class="capability-grid">
            <div class="capability-card" v-for="cap in mdmCapabilities" :key="cap.name">
              <div class="capability-icon">{{ cap.icon }}</div>
              <h4>{{ cap.name }}</h4>
              <p>{{ cap.desc }}</p>
            </div>
          </div>

          <h3>7.2 实施路线图</h3>
          <div class="impl-timeline">
            <div class="impl-phase" v-for="(phase, i) in implPhases" :key="i">
              <div class="impl-phase-header" :style="{ background: phase.color }">
                <span class="impl-phase-num">Phase {{ i + 1 }}</span>
                <span class="impl-phase-name">{{ phase.title }}</span>
              </div>
              <div class="impl-phase-body">
                <p>{{ phase.desc }}</p>
                <div class="impl-phase-duration">⏱ {{ phase.duration }}</div>
              </div>
            </div>
          </div>

          <div class="visual-block blue-gradient" style="margin-top: 32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">📝</div>
              <div>
                <h4>结语</h4>
                <p>主数据治理是一场"持久战"，不可能一蹴而就。成功的关键在于：选择正确的起点（从最痛的主数据域开始）、建立长效机制（组织+制度+工具）、持续度量改进（用数据说话）。古杉数据愿与您携手，打造坚实的数据资产基座。</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- CTA -->
      <section class="cta-section">
        <h2>需要主数据治理咨询？</h2>
        <p>古杉数据拥有丰富的央企主数据治理项目经验，助您从0到1建设MDM体系</p>
        <div class="cta-buttons">
          <router-link to="/appointment" class="btn-primary">预约主数据治理专家</router-link>
          <router-link to="/consulting/data-governance" class="btn-outline">了解数据治理咨询</router-link>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
export default {
  name: 'MasterDataManagement',
  data() {
    return {
      mdFeatures: [
        { icon: '🌐', title: '跨系统共享', desc: '在ERP、CRM、SRM、MES等多个系统中被引用和共享' },
        { icon: '⏳', title: '相对稳定', desc: '变化频率低于交易数据，但一旦变更影响范围大' },
        { icon: '🏷️', title: '唯一标识', desc: '每条主数据有全局唯一的编码标识' },
        { icon: '💎', title: '高业务价值', desc: '直接影响业务流程运转和管理决策准确性' }
      ],
      mdTypes: [
        { icon: '👤', name: '客户主数据', desc: '企业的所有客户统一视图', color: '#1A73E8', examples: ['客户名称', '统一社会信用代码', '客户分类', '信用额度'] },
        { icon: '📦', name: '产品/物料主数据', desc: '产品与物料的统一编码和属性', color: '#10B981', examples: ['物料编码', 'BOM结构', '计量单位', '物料分类'] },
        { icon: '🏭', name: '供应商主数据', desc: '供应商统一准入与管理', color: '#F59E0B', examples: ['供应商编码', '资质信息', '评级等级', '银行信息'] },
        { icon: '🏢', name: '组织架构主数据', desc: '企业组织和人员的统一结构', color: '#8B5CF6', examples: ['组织层级', '部门编码', '岗位体系', '汇报关系'] }
      ],
      challenges: [
        { title: '多系统编码不统一', desc: '同一客户/物料在不同系统中使用不同编码，导致数据无法关联、报表出错。', example: '某集团同一供应商在ERP中编码为V0001，在SRM中编码为SUP-001，在财务系统中又是另一套编码。', color: '#DC2626' },
        { title: '数据创建入口分散', desc: '各子公司、各系统都可以自行创建主数据，缺乏统一审核，导致重复和冲突。', example: '某制造企业20个工厂各自维护物料编码，同一种螺丝有17个不同的编码。', color: '#EA580C' },
        { title: '数据变更不同步', desc: '主数据在一个系统中变更后，无法及时同步到其他系统，导致数据不一致。', example: '客户地址在CRM中已更新，但ERP中的发货地址仍然是旧地址，导致发货错误。', color: '#CA8A04' },
        { title: '缺乏数据责任人', desc: '没有明确谁对主数据质量负责，出了问题推诿扯皮。', example: '产品主数据出错，研发说是生产录入的，生产说是采购提供的，没人修正。', color: '#2563EB' },
        { title: '历史数据清洗困难', desc: '多年积累的脏数据量大、规则复杂、牵连系统多，清洗成本高、风险大。', example: '某企业ERP中有100万+物料编码，其中35%已无交易记录，但不敢轻易删除。', color: '#7C3AED' }
      ],
      customerBasicFields: ['客户编码', '客户名称', '简称', '统一社会信用代码', '客户类型', '客户分类', '所属行业', '注册地址', '成立日期', '注册资本'],
      customerContactFields: ['联系人姓名', '联系电话', '联系邮箱', '联系地址', '联系人职务', '首选联系方式'],
      customerBizFields: ['客户等级', '信用额度', '付款条件', '合作状态', '负责销售', '所属区域', '首次合作日期'],
      customerFinFields: ['开户银行', '银行账号', '发票类型', '税号', '结算币种', '应收账款限额'],
      codeSegments: [
        { name: '前缀', desc: '客户类型标识（C=企业客户，P=个人客户）', example: 'C', color: '#1A73E8' },
        { name: '区域码', desc: '2位区域编码（如01=华北，02=华东）', example: '02', color: '#10B981' },
        { name: '序列号', desc: '6位流水号，自动递增', example: '000128', color: '#F59E0B' },
        { name: '校验位', desc: '1位校验码，防止录入错误', example: '7', color: '#8B5CF6' }
      ],
      productFlow: [
        { title: '需求提交', desc: '业务部门提交新增/变更申请，填写标准化申请表', roles: ['业务申请人'], color: '#4A90D9' },
        { title: '数据管家审核', desc: '主数据管家检查重复性、规范性、完整性', roles: ['数据管家'], color: '#1F6FEB' },
        { title: '编码分配', desc: '按编码规则分配唯一编码，录入主数据管理平台', roles: ['编码管理员'], color: '#0E4EAD' },
        { title: '审批发布', desc: '业务负责人审批后发布，自动同步至下游系统', roles: ['业务负责人', 'IT管理员'], color: '#0A3A82' },
        { title: '日常维护', desc: '定期review数据质量，处理变更请求', roles: ['数据管家', '数据责任人'], color: '#0E1F52' }
      ],
      orgLevels: [
        { name: '集团', desc: '集团总部，顶级组织节点', color: '#0E1F52', attrs: ['集团编码', '集团名称', '组织形态'] },
        { name: '公司', desc: '独立法人实体，财务核算单元', color: '#1F6FEB', attrs: ['公司编码', '法人代表', '注册地', '税务信息'] },
        { name: '部门', desc: '职能部门或事业部', color: '#4A90D9', attrs: ['部门编码', '部门名称', '上级部门', '部门职责'] },
        { name: '岗位', desc: '具体工作岗位', color: '#93C5FD', attrs: ['岗位编码', '岗位名称', '岗位等级', '编制人数'] }
      ],
      orgQAs: [
        { q: '组织架构频繁调整怎么办？', a: '采用版本化管理，每次调整生成新版本，历史版本可追溯。同时设置"生效日期"字段，支持未来生效。' },
        { q: '矩阵式组织如何建模？', a: '主维度为行政组织，辅以项目组织、虚拟组织等维度。通过"人员-组织"多对多关系表实现一人多挂靠。' },
        { q: '组织编码变更影响太大怎么办？', a: '组织编码一经分配不应变更。组织名称可变更，通过编码保持数据连续性。废止的组织标记为"停用"而非删除。' },
        { q: '境外公司如何纳入统一体系？', a: '设置组织类型字段区分境内/境外，编码体系统一，属性字段兼容国际化需求（多语言、多币种）。' }
      ],
      qualityDims: [
        { icon: '✅', name: '完整性', desc: '必填字段是否完整填写', rule: '客户主数据必填字段填写率 ≥ 目标值', target: '≥ 98%', color: '#10B981' },
        { icon: '🔍', name: '准确性', desc: '字段值是否符合业务规则', rule: '统一社会信用代码格式校验通过率', target: '≥ 99%', color: '#3B82F6' },
        { icon: '🔄', name: '一致性', desc: '同一数据在多系统间是否一致', rule: 'ERP与CRM客户名称一致性比对', target: '≥ 95%', color: '#F59E0B' },
        { icon: '⏱', name: '及时性', desc: '数据创建和变更的响应速度', rule: '主数据创建工单48小时内完成率', target: '≥ 90%', color: '#8B5CF6' },
        { icon: '🚫', name: '唯一性', desc: '是否存在重复记录', rule: '客户主数据重复率（按社会信用代码去重）', target: '≤ 0.5%', color: '#DC2626' },
        { icon: '🔗', name: '关联性', desc: '关联数据是否正确匹配', rule: '客户-销售组织映射完整率', target: '≥ 97%', color: '#EA580C' }
      ],
      monitorLoop: [
        { icon: '📊', title: '自动检测', desc: '质量规则引擎定时扫描主数据，发现不合规记录', color: '#4A90D9' },
        { icon: '🔔', title: '告警通知', desc: '质量问题自动生成工单，推送给数据责任人', color: '#1F6FEB' },
        { icon: '🔧', title: '整改处理', desc: '数据责任人在SLA时限内完成数据修复', color: '#0E4EAD' },
        { icon: '✅', title: '验证确认', desc: '修复后再次运行质量规则，确认问题已解决', color: '#0E1F52' }
      ],
      mdmCapabilities: [
        { icon: '🏗️', name: '数据建模', desc: '灵活定义主数据模型，支持自定义属性和关系' },
        { icon: '🔄', name: '数据集成', desc: '与ERP/CRM/SRM等系统双向同步，支持实时和批量' },
        { icon: '📋', name: '工作流引擎', desc: '主数据创建、变更、审批的流程化管理' },
        { icon: '🔍', name: '查重匹配', desc: '智能识别重复记录，支持模糊匹配和合并' },
        { icon: '✅', name: '质量管理', desc: '内置质量规则引擎，自动检查和告警' },
        { icon: '📊', name: '数据分析', desc: '主数据质量看板、使用分析、覆盖率统计' }
      ],
      implPhases: [
        { title: '试点启动', desc: '选择1个主数据域（如客户或物料）在1-2个系统上试点', duration: '2-3个月', color: '#4A90D9' },
        { title: '标准推广', desc: '将试点成果推广到更多主数据域和更多系统', duration: '3-6个月', color: '#1F6FEB' },
        { title: '全面覆盖', desc: '实现全集团主数据统一管理，打通所有核心系统', duration: '6-12个月', color: '#0E4EAD' },
        { title: '持续运营', desc: '建立长效运营机制，持续优化数据质量', duration: '持续', color: '#0E1F52' }
      ]
    }
  }
}
</script>

<style scoped>
.article-page { background: #fff; min-height: 100vh; }

.breadcrumb-nav { max-width: 960px; margin: 0 auto; padding: 20px 24px; font-size: 14px; color: #5A5E66; }
.breadcrumb-nav a { color: #1A73E8; text-decoration: none; }
.breadcrumb-nav a:hover { text-decoration: underline; }
.breadcrumb-nav .separator { margin: 0 8px; color: #ccc; }
.breadcrumb-nav .current { color: #0E1F52; }

.container { max-width: 960px; margin: 0 auto; padding: 0 24px 60px; }

.article-header { text-align: center; padding: 40px 0 32px; border-bottom: 1px solid #E8ECF0; margin-bottom: 40px; }
.article-header .article-tags { display: flex; justify-content: center; gap: 8px; margin-bottom: 20px; }
.article-header .tag { background: #EBF3FE; color: #1A73E8; padding: 4px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; }
.article-title { font-size: 2.2rem; font-weight: 800; color: #0E1F52; line-height: 1.3; margin-bottom: 16px; }
.article-subtitle { font-size: 1.1rem; color: #5A5E66; margin-bottom: 20px; }
.article-meta { display: flex; justify-content: center; gap: 24px; font-size: 14px; color: #8C8C8C; }

.intro-card { display: flex; gap: 16px; background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-left: 4px solid #1A73E8; border-radius: 12px; padding: 24px; margin-bottom: 40px; }
.intro-card .intro-icon { font-size: 2rem; flex-shrink: 0; }
.intro-card h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.intro-card p { font-size: 15px; color: #3D4150; line-height: 1.7; }

.toc { background: #F6F8FC; border-radius: 12px; padding: 24px 28px; margin-bottom: 48px; }
.toc h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.toc ul { list-style: none; padding: 0; }
.toc li { margin-bottom: 8px; }
.toc a { color: #1A73E8; text-decoration: none; font-size: 15px; }
.toc a:hover { text-decoration: underline; }

.article-body { font-size: 16px; line-height: 1.85; color: #3D4150; }
.article-body p { margin-bottom: 18px; }
.chapter { margin-bottom: 56px; }
.chapter h2 { font-size: 1.6rem; font-weight: 800; color: #0E1F52; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 3px solid #1A73E8; display: inline-block; }
.chapter h3 { font-size: 1.3rem; font-weight: 700; color: #0E1F52; margin: 28px 0 16px; }

.visual-block { border-radius: 12px; padding: 24px; margin: 24px 0; }
.blue-gradient { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; }
.visual-block-inner { display: flex; gap: 16px; align-items: center; }
.visual-icon { font-size: 2.5rem; flex-shrink: 0; }
.visual-block h4 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.visual-block p { font-size: 15px; line-height: 1.6; opacity: 0.9; margin: 0; }

.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 28px 0; }
.stat-card { background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-radius: 12px; padding: 24px; text-align: center; border: 1px solid #D6E4F0; }
.stat-number { font-size: 1.8rem; font-weight: 900; color: #1A73E8; margin-bottom: 8px; }
.stat-label { font-size: 13px; color: #5A5E66; line-height: 1.5; }

.highlight-box { display: flex; gap: 16px; background: #FFF7E6; border-left: 4px solid #FAAD14; border-radius: 12px; padding: 24px; margin: 28px 0; }
.highlight-icon { font-size: 2rem; flex-shrink: 0; }
.highlight-box h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.highlight-box p { font-size: 15px; color: #3D4150; line-height: 1.7; margin: 0; }

/* 特征卡片 */
.feature-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.feature-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; text-align: center; }
.feature-icon { font-size: 2rem; margin-bottom: 8px; }
.feature-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.feature-card p { font-size: 13px; color: #5A5E66; margin: 0; line-height: 1.5; }

/* 主数据类型 */
.md-types { margin: 24px 0; }
.md-type-card { display: flex; gap: 16px; background: #F6F8FC; border-radius: 12px; padding: 20px; margin-bottom: 12px; border: 1px solid #E8ECF0; }
.md-type-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; flex-shrink: 0; color: #fff; }
.md-type-card h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.md-type-card > div > p { font-size: 14px; color: #5A5E66; margin-bottom: 8px; }
.md-type-examples { display: flex; gap: 8px; flex-wrap: wrap; }
.md-type-examples span { background: #EBF3FE; color: #1A73E8; padding: 3px 10px; border-radius: 6px; font-size: 12px; }

/* 挑战 */
.challenge-list { position: relative; padding-left: 40px; margin: 24px 0; }
.challenge-list::before { content: ''; position: absolute; left: 19px; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom, #DC2626, #7C3AED); border-radius: 2px; }
.challenge-item { position: relative; margin-bottom: 20px; }
.challenge-marker { position: absolute; left: -40px; width: 38px; height: 38px; border-radius: 50%; color: #fff; font-size: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; z-index: 1; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
.challenge-content { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.challenge-content h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.challenge-content p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin-bottom: 8px; }
.challenge-example { font-size: 13px; color: #DC2626; background: #FEF2F2; padding: 8px 14px; border-radius: 8px; }

/* 数据模型 */
.data-model { margin: 24px 0; }
.model-layer { background: #F6F8FC; border-radius: 12px; padding: 16px 24px; margin-bottom: 8px; border: 1px solid #E8ECF0; }
.layer-title { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.layer-fields { display: flex; gap: 8px; flex-wrap: wrap; }
.layer-fields span { background: #0E1F52; color: #fff; padding: 4px 12px; border-radius: 6px; font-size: 12px; }

/* 编码 */
.code-rule { display: flex; gap: 4px; margin: 24px 0; }
.code-segment { flex: 1; border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; }
.code-segment-header { color: #fff; padding: 10px; text-align: center; font-size: 14px; font-weight: 700; }
.code-segment-body { padding: 16px; text-align: center; }
.code-segment-body p { font-size: 13px; color: #5A5E66; margin-bottom: 8px; }
.code-example { font-size: 1.5rem; font-weight: 900; color: #0E1F52; font-family: monospace; }

/* 流程 */
.flow-steps { position: relative; padding-left: 40px; margin: 24px 0; }
.flow-steps::before { content: ''; position: absolute; left: 19px; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom, #4A90D9, #0E1F52); border-radius: 2px; }
.flow-step { position: relative; margin-bottom: 20px; }
.flow-step:last-child { margin-bottom: 0; }
.flow-step-number { position: absolute; left: -40px; width: 38px; height: 38px; border-radius: 50%; color: #fff; font-size: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; z-index: 1; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
.flow-step-content { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.flow-step-content h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.flow-step-content p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin-bottom: 8px; }
.role-tag { background: #EBF3FE; color: #1A73E8; padding: 3px 10px; border-radius: 6px; font-size: 12px; margin-right: 6px; }

/* BOM */
.bom-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.bom-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.bom-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.bom-card ul { list-style: none; padding: 0; margin: 0; }
.bom-card li { font-size: 13px; color: #5A5E66; padding: 4px 0 4px 14px; position: relative; }
.bom-card li::before { content: '▸'; position: absolute; left: 0; color: #1A73E8; font-weight: 700; }

/* 组织模型 */
.org-model { margin: 24px 0; }
.org-level { display: flex; gap: 16px; align-items: center; margin-bottom: 12px; }
.org-level-badge { color: #fff; padding: 8px 20px; border-radius: 8px; font-size: 14px; font-weight: 700; flex-shrink: 0; min-width: 60px; text-align: center; }
.org-level-desc { font-size: 14px; color: #5A5E66; margin-bottom: 6px; }
.org-level-attrs { display: flex; gap: 8px; flex-wrap: wrap; }
.org-level-attrs span { background: #EBF3FE; color: #1A73E8; padding: 3px 10px; border-radius: 6px; font-size: 12px; }

/* QA */
.qa-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin: 24px 0; }
.qa-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.qa-question { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 10px; }
.qa-answer { font-size: 14px; color: #3D4150; line-height: 1.6; }

/* 质量维度 */
.quality-dims { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.quality-dim { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; border-top: 3px solid; }
.dim-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.dim-icon { font-size: 1.5rem; }
.dim-header h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin: 0; }
.quality-dim > p { font-size: 13px; color: #5A5E66; margin-bottom: 8px; }
.dim-rule { font-size: 12px; color: #1A73E8; background: #EBF3FE; padding: 6px 10px; border-radius: 6px; margin-bottom: 6px; }
.dim-target { font-size: 13px; font-weight: 700; color: #10B981; }

/* 监控闭环 */
.loop-flow { display: flex; gap: 8px; margin: 24px 0; justify-content: center; }
.loop-step { flex: 1; text-align: center; background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.loop-marker { width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 12px; font-size: 1.5rem; }
.loop-step h4 { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.loop-step p { font-size: 12px; color: #5A5E66; margin: 0; }

/* 能力 */
.capability-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.capability-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; text-align: center; transition: all 0.3s; }
.capability-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31,63,143,0.1); }
.capability-icon { font-size: 2rem; margin-bottom: 8px; }
.capability-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.capability-card p { font-size: 13px; color: #5A5E66; margin: 0; }

/* 实施 */
.impl-timeline { display: flex; gap: 8px; margin: 24px 0; }
.impl-phase { flex: 1; border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; }
.impl-phase-header { padding: 16px; color: #fff; text-align: center; }
.impl-phase-num { display: block; font-size: 12px; opacity: 0.8; margin-bottom: 4px; }
.impl-phase-name { font-size: 15px; font-weight: 700; }
.impl-phase-body { padding: 16px; }
.impl-phase-body p { font-size: 13px; color: #5A5E66; margin-bottom: 8px; line-height: 1.5; }
.impl-phase-duration { font-size: 13px; color: #1A73E8; font-weight: 600; }

/* CTA */
.cta-section { text-align: center; background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; border-radius: 16px; padding: 48px 32px; margin-bottom: 60px; }
.cta-section h2 { font-size: 1.8rem; font-weight: 800; margin-bottom: 12px; }
.cta-section p { font-size: 16px; opacity: 0.85; margin-bottom: 28px; }
.cta-buttons { display: flex; justify-content: center; gap: 16px; }
.btn-primary { background: #1A73E8; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; transition: all 0.3s; }
.btn-primary:hover { background: #1557B0; }
.btn-outline { background: transparent; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; border: 2px solid rgba(255,255,255,0.5); transition: all 0.3s; }
.btn-outline:hover { border-color: #fff; background: rgba(255,255,255,0.1); }

@media (max-width: 768px) {
  .article-title { font-size: 1.6rem; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .feature-grid { grid-template-columns: repeat(2, 1fr); }
  .quality-dims { grid-template-columns: 1fr; }
  .bom-grid { grid-template-columns: 1fr; }
  .qa-grid { grid-template-columns: 1fr; }
  .capability-grid { grid-template-columns: 1fr; }
  .impl-timeline { flex-direction: column; }
  .loop-flow { flex-direction: column; }
  .code-rule { flex-direction: column; }
  .cta-buttons { flex-direction: column; align-items: center; }
}
@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr; }
  .feature-grid { grid-template-columns: 1fr; }
}
</style>
